// Spades in VR: the join panel over the table, then — seated — a panel in
// your lap with the lobby / bid picker / your hand as tappable cards, the
// score, Leave, the rules and the leaderboard. Table-local, floor origin.

import React, { useState } from 'react';
import { Text } from '@react-three/drei';
import { tableAction, handOf } from '../gameStore';
import { leaveTable } from '../kit/LobbyKit';
import { VRBtn, VRJoinPanel, VRLobbyBody, VRRulesPanel, VRLeaderboardPanel, SeatPanelMount, vrColors as colors, noRaycast, Surface } from '../kit/vrKit';
import { cardLabel, isRed } from '../cardFaces';
import { legalPlays, SEAT_NAMES, type SpadesState } from '../../../../supabase/functions/games/spades.ts';
import type { VRPanelProps } from '../types';

function VRCard({ card, x, y, playable, onPress }: { card: string; x: number; y: number; playable: boolean; onPress: () => void }) {
  const [hover, setHover] = useState(false);
  const w = 0.052, h = 0.074;
  return (
    <group position={[x, y + (hover && playable ? 0.01 : 0), 0.006]} name={`vr-card-${card}`}
      onPointerOver={(e) => { e.stopPropagation(); setHover(true); }} onPointerOut={() => setHover(false)}
      onClick={(e) => { e.stopPropagation(); if (playable) onPress(); }}>
      <Surface width={w} height={h} color={playable ? '#fbfaf6' : '#9aa0a6'} r={0.006} />
      <Text raycast={noRaycast} position={[0, 0, 0.003]} fontSize={0.022} color={isRed(card) ? '#c8102e' : '#16181d'}>{cardLabel(card)}</Text>
    </group>
  );
}

export function SpadesVRPanel({ def, pid, worldId, entry, table, seat }: VRPanelProps) {
  const [rules, setRules] = useState(false);
  const [board, setBoard] = useState(false);
  const st = table.state;
  const g = st.g as SpadesState | null;
  const W = 0.8;

  // Not seated: a join panel floating over the table.
  if (seat < 0) return <VRJoinPanel def={def} pid={pid} entry={entry} worldId={worldId} />;

  const hand = handOf(entry);
  const myTurn = !!g && st.phase === 'playing' && g.turn === seat;
  const legal = g && myTurn && g.phase === 'playing' ? new Set(legalPlays(g, hand)) : new Set<string>();

  let body: React.ReactNode = null;
  let H = 0.36;
  if (st.phase === 'lobby') {
    body = <VRLobbyBody def={def} pid={pid} entry={entry} />;
  } else if (st.phase === 'over' && g) {
    body = <>
      <Text raycast={noRaycast} position={[0, 0.1, 0.004]} fontSize={0.024} color={colors.teal}>{`${g.winner === seat % 2 ? 'You win!' : 'They win'}  ${g.scores[0]} – ${g.scores[1]}`}</Text>
      <VRBtn label="Rematch" x={-0.19} y={0.02} w={0.34} active disabled={entry.busy} onPress={() => void tableAction(pid, 'rematch')} />
      <VRBtn name="vr-game-leaders-over" label="Leaderboard" x={0.19} y={0.02} w={0.34} active={board} onPress={() => { setRules(false); setBoard((v) => !v); }} />
    </>;
  } else if (g && g.phase === 'bidding' && myTurn) {
    H = 0.42;
    body = <>
      <Text raycast={noRaycast} position={[0, 0.165, 0.004]} fontSize={0.018} color={colors.text}>Your bid</Text>
      {Array.from({ length: 14 }, (_, b) => (
        <VRBtn key={b} name={`vr-game-bid-${b}`} label={b === 0 ? 'Nil' : String(b)} x={-0.33 + (b % 7) * 0.11} y={0.115 - Math.floor(b / 7) * 0.065} w={0.095} h={0.055}
          disabled={entry.busy} onPress={() => void tableAction(pid, 'move', { move: { type: 'bid', bid: b } })} />
      ))}
    </>;
  } else if (g) {
    const turnName = st.seats[g.turn]?.name ?? SEAT_NAMES[g.turn];
    const status = g.phase === 'hand_over' ? 'Hand over — next deal soon'
      : myTurn ? (g.phase === 'playing' ? 'Your turn — tap a card' : 'Your bid')
      : `${turnName}'s turn`;
    body = <Text raycast={noRaycast} position={[0, 0.12, 0.004]} fontSize={0.02} color={myTurn ? colors.teal : colors.text}>{status}</Text>;
  }

  // My hand: up to two rows of 7.
  const rows = [hand.slice(0, 7), hand.slice(7)];
  return (
    <SeatPanelMount chair={def.chairs[seat]} height={H}>
      <Surface width={W} height={H} color={colors.panel} />
      <group position={[0, H / 2 - 0.2, 0]}>{body}</group>
      {g && st.phase === 'playing' && hand.length > 0 && rows.map((row, ri) => row.map((c, k) => (
        <VRCard key={c} card={c} x={-((row.length - 1) * 0.058) / 2 + k * 0.058} y={-H / 2 + 0.14 - ri * 0.082}
          playable={legal.has(c) && !entry.busy}
          onPress={() => void tableAction(pid, 'move', { move: { type: 'play', card: c } })} />
      )))}
      <VRBtn label="Leave" x={W / 2 - 0.07} y={H / 2 - 0.035} w={0.11} h={0.04} fontSize={0.014} onPress={() => leaveTable(pid)} />
      <VRBtn name="vr-game-howto" label="?" x={W / 2 - 0.155} y={H / 2 - 0.035} w={0.045} h={0.04} fontSize={0.016} active={rules} onPress={() => { setBoard(false); setRules((v) => !v); }} />
      <VRBtn name="vr-game-leaders" label="Leaders" x={W / 2 - 0.25} y={H / 2 - 0.035} w={0.12} h={0.04} fontSize={0.014} active={board} onPress={() => { setRules(false); setBoard((v) => !v); }} />
      {/* Over the seated panel (not above it — that would float over your head). */}
      {rules && <group position={[0, 0, 0.02]}><VRRulesPanel def={def} y={-0.31} onClose={() => setRules(false)} /></group>}
      {board && <group position={[0, 0, 0.02]}><VRLeaderboardPanel def={def} worldId={worldId} refreshKey={st.gameId} onClose={() => setBoard(false)} /></group>}
      {g && <Text raycast={noRaycast} position={[-W / 2 + 0.02, H / 2 - 0.035, 0.004]} anchorX="left" fontSize={0.014} color={colors.muted}>
        {`Us ${g.scores[seat % 2]}  ·  Them ${g.scores[(seat + 1) % 2]}`}
      </Text>}
      {entry.error && <Text raycast={noRaycast} position={[0, -H / 2 + 0.02, 0.004]} fontSize={0.013} color="#ff9b9b">{entry.error}</Text>}
    </SeatPanelMount>
  );
}

export default SpadesVRPanel;
